<script setup>
import { ref, computed, watch, inject, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import pilotAssistantAPI from 'dashboard/api/pilot/assistant';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import RuleCard from 'dashboard/components-next/pilot/assistant/RuleCard.vue';
import SuggestedRules from 'dashboard/components-next/pilot/assistant/SuggestedRules.vue';
import AddNewRulesInput from 'dashboard/components-next/pilot/assistant/AddNewRulesInput.vue';
import {
  createRule,
  deleteRuleById,
  hasTemplateRule,
  updateRuleContent,
} from 'dashboard/utils/pilot/rules';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const store = useStore();

// 注入注册保存处理器的函数
const registerSaveHandler = inject('registerSaveHandler', null);

// 预制数据（从后端 API 获取）
const presetGuardrails = ref([]);
const presetGuidelines = ref([]);

// 人设与角色定义
const personaContent = ref('');
const PERSONA_MAX_LENGTH = 5000;
const isPersonaSaving = ref(false);

// 开场白
const greetingContent = ref('');
const GREETING_MAX_LENGTH = 1000;
const isGreetingSaving = ref(false);

// 从 props.assistant 初始化数据
watch(
  () => props.assistant,
  newAssistant => {
    if (newAssistant) {
      personaContent.value = newAssistant.persona || '';
      greetingContent.value = newAssistant.greeting || '';
    }
  },
  { immediate: true }
);

// 保存人设（失焦时触发）
const handlePersonaBlur = async () => {
  // 如果内容没变化，不保存
  if (personaContent.value === (props.assistant?.persona || '')) return;

  isPersonaSaving.value = true;
  try {
    await store.dispatch('pilotAssistants/update', {
      id: props.assistant.id,
      data: { persona: personaContent.value },
    });
    useAlert(t('PILOT.ASSISTANTS.FORM.PERSONA.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(t('PILOT.ASSISTANTS.FORM.PERSONA.SAVE_ERROR'));
  } finally {
    isPersonaSaving.value = false;
  }
};

// 保存开场白（失焦时触发）
const handleGreetingBlur = async () => {
  // 如果内容没变化，不保存
  if (greetingContent.value === (props.assistant?.greeting || '')) return;

  isGreetingSaving.value = true;
  try {
    await store.dispatch('pilotAssistants/update', {
      id: props.assistant.id,
      data: { greeting: greetingContent.value },
    });
    useAlert(t('PILOT.ASSISTANTS.FORM.GREETING.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(t('PILOT.ASSISTANTS.FORM.GREETING.SAVE_ERROR'));
  } finally {
    isGreetingSaving.value = false;
  }
};

// 保存人设和开场白（用于顶部保存按钮）
const savePersonaAndGreeting = async () => {
  try {
    await store.dispatch('pilotAssistants/update', {
      id: props.assistant.id,
      data: {
        persona: personaContent.value,
        greeting: greetingContent.value,
      },
    });
    useAlert(t('PILOT.ASSISTANTS.CONFIG.SAVE_SUCCESS'));
    return true;
  } catch (error) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.message ||
      t('PILOT.ASSISTANTS.CONFIG.SAVE_ERROR');
    useAlert(errorMessage);
    return false;
  }
};

// 注册保存处理器
onMounted(async () => {
  if (registerSaveHandler) {
    registerSaveHandler(savePersonaAndGreeting);
  }

  // 获取预制数据
  try {
    const response = await pilotAssistantAPI.getPresetData();
    presetGuardrails.value = response.data.guardrails || [];
    presetGuidelines.value = response.data.guidelines || [];
  } catch (error) {
    // 如果获取失败，使用空数组
    console.error('Failed to load preset data:', error);
  }
});

onUnmounted(() => {
  if (registerSaveHandler) {
    registerSaveHandler(null);
  }
});

// 约束与安全网
const newGuardrail = ref('');
const hoveredGuardrailCard = ref(null);
const showGuardrailsSuggestions = ref(true);
const guardrailsCollapsed = ref(
  localStorage.getItem('pilot_guardrails_collapsed') === 'true'
);

const guardrails = computed(() => props.assistant?.guardrails || []);

const displayGuardrails = computed(() => guardrails.value);

const shouldShowGuardrailsSuggestions = computed(() => {
  return showGuardrailsSuggestions.value;
});

watch(guardrails, newVal => {
  if (newVal.length === 0) {
    showGuardrailsSuggestions.value = true;
    guardrailsCollapsed.value = false;
    localStorage.removeItem('pilot_guardrails_collapsed');
  }
});

// 使用从 API 获取的预制数据
const guardrailsExample = computed(() => presetGuardrails.value);

const closeGuardrailsSuggestions = () => {
  guardrailsCollapsed.value = true;
  localStorage.setItem('pilot_guardrails_collapsed', 'true');
};

const expandGuardrailsSuggestions = () => {
  guardrailsCollapsed.value = false;
  localStorage.setItem('pilot_guardrails_collapsed', 'false');
};

const addAllGuardrailsExample = async () => {
  const newRules = guardrailsExample.value
    .filter(example => !hasTemplateRule(guardrails.value, example.key))
    .map(example =>
      createRule({ content: example.content, templateKey: example.key })
    );
  const updated = [...guardrails.value, ...newRules];

  try {
    await store.dispatch('pilotAssistants/update', {
      id: props.assistant.id,
      data: { guardrails: updated },
    });
    showGuardrailsSuggestions.value = false;
    useAlert(t('PILOT.ASSISTANTS.GUARDRAILS.API.ADD.SUCCESS'));
  } catch {
    useAlert(t('PILOT.ASSISTANTS.GUARDRAILS.API.ADD.ERROR'));
  }
};

const handleGuardrailHover = (isHovered, id) => {
  hoveredGuardrailCard.value = isHovered ? id : null;
};

const addGuardrail = async (content, templateKey = null) => {
  if (!content?.trim()) return;

  try {
    const updated = [
      ...guardrails.value,
      createRule({ content: content.trim(), templateKey }),
    ];
    await store.dispatch('pilotAssistants/update', {
      id: props.assistant.id,
      data: { guardrails: updated },
    });
    useAlert(t('PILOT.ASSISTANTS.GUARDRAILS.API.ADD.SUCCESS'));
    newGuardrail.value = '';
  } catch {
    useAlert(t('PILOT.ASSISTANTS.GUARDRAILS.API.ADD.ERROR'));
  }
};

const editGuardrail = async ({ id, content }) => {
  try {
    const updated = updateRuleContent(guardrails.value, id, content);
    await store.dispatch('pilotAssistants/update', {
      id: props.assistant.id,
      data: { guardrails: updated },
    });
    useAlert(t('PILOT.ASSISTANTS.GUARDRAILS.API.UPDATE.SUCCESS'));
  } catch {
    useAlert(t('PILOT.ASSISTANTS.GUARDRAILS.API.UPDATE.ERROR'));
  }
};

const deleteGuardrail = async id => {
  try {
    const updated = deleteRuleById(guardrails.value, id);
    await store.dispatch('pilotAssistants/update', {
      id: props.assistant.id,
      data: { guardrails: updated },
    });
    useAlert(t('PILOT.ASSISTANTS.GUARDRAILS.API.DELETE.SUCCESS'));
  } catch {
    useAlert(t('PILOT.ASSISTANTS.GUARDRAILS.API.DELETE.ERROR'));
  }
};

// 回复指南
const newGuideline = ref('');
const hoveredGuidelineCard = ref(null);
const showGuidelinesSuggestions = ref(true);
const guidelinesCollapsed = ref(
  localStorage.getItem('pilot_guidelines_collapsed') === 'true'
);

const guidelines = computed(() => props.assistant?.response_guidelines || []);

const displayGuidelines = computed(() => guidelines.value);

const shouldShowGuidelinesSuggestions = computed(() => {
  return showGuidelinesSuggestions.value;
});

watch(guidelines, newVal => {
  if (newVal.length === 0) {
    showGuidelinesSuggestions.value = true;
    guidelinesCollapsed.value = false;
    localStorage.removeItem('pilot_guidelines_collapsed');
  }
});

// 使用从 API 获取的预制数据
const guidelinesExample = computed(() => presetGuidelines.value);

const closeGuidelinesSuggestions = () => {
  guidelinesCollapsed.value = true;
  localStorage.setItem('pilot_guidelines_collapsed', 'true');
};

const expandGuidelinesSuggestions = () => {
  guidelinesCollapsed.value = false;
  localStorage.setItem('pilot_guidelines_collapsed', 'false');
};

const addAllGuidelinesExample = async () => {
  const newRules = guidelinesExample.value
    .filter(example => !hasTemplateRule(guidelines.value, example.key))
    .map(example =>
      createRule({ content: example.content, templateKey: example.key })
    );
  const updated = [...guidelines.value, ...newRules];

  try {
    await store.dispatch('pilotAssistants/update', {
      id: props.assistant.id,
      data: { response_guidelines: updated },
    });
    showGuidelinesSuggestions.value = false;
    useAlert(t('PILOT.ASSISTANTS.RESPONSE_GUIDELINES.API.ADD.SUCCESS'));
  } catch {
    useAlert(t('PILOT.ASSISTANTS.RESPONSE_GUIDELINES.API.ADD.ERROR'));
  }
};

const handleGuidelineHover = (isHovered, id) => {
  hoveredGuidelineCard.value = isHovered ? id : null;
};

const addGuideline = async (content, templateKey = null) => {
  if (!content?.trim()) return;

  try {
    const updated = [
      ...guidelines.value,
      createRule({ content: content.trim(), templateKey }),
    ];
    await store.dispatch('pilotAssistants/update', {
      id: props.assistant.id,
      data: { response_guidelines: updated },
    });
    useAlert(t('PILOT.ASSISTANTS.RESPONSE_GUIDELINES.API.ADD.SUCCESS'));
    newGuideline.value = '';
  } catch {
    useAlert(t('PILOT.ASSISTANTS.RESPONSE_GUIDELINES.API.ADD.ERROR'));
  }
};

const editGuideline = async ({ id, content }) => {
  try {
    const updated = updateRuleContent(guidelines.value, id, content);
    await store.dispatch('pilotAssistants/update', {
      id: props.assistant.id,
      data: { response_guidelines: updated },
    });
    useAlert(t('PILOT.ASSISTANTS.RESPONSE_GUIDELINES.API.UPDATE.SUCCESS'));
  } catch {
    useAlert(t('PILOT.ASSISTANTS.RESPONSE_GUIDELINES.API.UPDATE.ERROR'));
  }
};

const deleteGuideline = async id => {
  try {
    const updated = deleteRuleById(guidelines.value, id);
    await store.dispatch('pilotAssistants/update', {
      id: props.assistant.id,
      data: { response_guidelines: updated },
    });
    useAlert(t('PILOT.ASSISTANTS.RESPONSE_GUIDELINES.API.DELETE.SUCCESS'));
  } catch {
    useAlert(t('PILOT.ASSISTANTS.RESPONSE_GUIDELINES.API.DELETE.ERROR'));
  }
};

// 暴露保存方法给父组件
defineExpose({
  savePersonaAndGreeting,
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- 一级标题 -->
    <h2 class="text-base font-semibold text-n-slate-12 pt-4">
      {{ t('PILOT.ASSISTANTS.CONFIG.NAV.ROLE') }}
    </h2>

    <!-- 人设与角色定义 -->
    <section class="bg-[#F3F4F7] dark:bg-n-slate-7/50 rounded-xl p-3">
      <div class="flex items-center gap-2 mb-2">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('PILOT.ASSISTANTS.FORM.PERSONA.LABEL') }}
        </h3>
        <span
          v-tooltip="t('PILOT.ASSISTANTS.FORM.PERSONA.HELP_TEXT')"
          class="i-lucide-help-circle text-sm text-n-slate-9 cursor-help"
        />
      </div>
      <TextArea
        v-model="personaContent"
        :max-length="PERSONA_MAX_LENGTH"
        :show-character-count="true"
        :auto-height="true"
        min-height="140px"
        max-height="400px"
        :placeholder="t('PILOT.ASSISTANTS.FORM.PERSONA.PLACEHOLDER')"
        custom-text-area-wrapper-class="!bg-white dark:!bg-n-slate-800"
        @blur="handlePersonaBlur"
      />
    </section>

    <!-- 约束与安全网 -->
    <section class="bg-[#F3F4F7] dark:bg-n-slate-7/50 rounded-xl p-3">
      <div class="mb-3">
        <div class="flex items-center gap-2">
          <h3 class="text-sm font-medium text-n-slate-12">约束与安全网</h3>
          <span
            v-tooltip="
              '对于 LLM 来说，多行结构化数据更能有效理解，将您的要求写到每一行'
            "
            class="i-lucide-help-circle text-sm text-n-slate-9 cursor-help"
          />
        </div>
        <p class="text-xs text-n-slate-10 mt-1">
          设定助手的回答边界，明确哪些话题不应涉及或需要谨慎处理，确保回复符合业务规范
        </p>
      </div>

      <!-- 建议规则 -->
      <div v-if="shouldShowGuardrailsSuggestions" class="mb-4">
        <SuggestedRules
          title="快速添加常用约束"
          :items="guardrailsExample"
          :collapsed="guardrailsCollapsed"
          @add="addAllGuardrailsExample"
          @close="closeGuardrailsSuggestions"
          @expand="expandGuardrailsSuggestions"
        >
          <template #default="{ item }">
            <div class="flex items-center justify-between w-full">
              <span class="text-sm text-n-slate-12">{{ item.content }}</span>
              <Button
                :label="
                  hasTemplateRule(guardrails, item.key) ? '已添加' : '添加'
                "
                ghost
                xs
                slate
                class="!text-sm !text-n-slate-11 flex-shrink-0"
                :disabled="hasTemplateRule(guardrails, item.key)"
                @click="addGuardrail(item.content, item.key)"
              />
            </div>
          </template>
        </SuggestedRules>
      </div>

      <!-- 规则列表 -->
      <div class="space-y-2">
        <div
          v-if="
            displayGuardrails.length === 0 && !shouldShowGuardrailsSuggestions
          "
          class="text-sm text-n-slate-10 py-2"
        >
          暂无约束规则，请在下方添加
        </div>
        <RuleCard
          v-for="guardrail in displayGuardrails"
          :id="guardrail.id"
          :key="guardrail.id"
          :content="guardrail.content"
          :selectable="false"
          @edit="editGuardrail"
          @delete="deleteGuardrail"
          @hover="isHovered => handleGuardrailHover(isHovered, guardrail.id)"
        />
        <AddNewRulesInput
          v-model="newGuardrail"
          placeholder="输入新的约束规则"
          label="添加"
          @add="addGuardrail"
        />
      </div>
    </section>

    <!-- 回复指南 -->
    <section class="bg-[#F3F4F7] dark:bg-n-slate-7/50 rounded-xl p-3">
      <div class="mb-3">
        <div class="flex items-center gap-2">
          <h3 class="text-sm font-medium text-n-slate-12">回复指南</h3>
          <span
            v-tooltip="
              '对于 LLM 来说，多行结构化数据更能有效理解，将您的要求写到每一行'
            "
            class="i-lucide-help-circle text-sm text-n-slate-9 cursor-help"
          />
        </div>
        <p class="text-xs text-n-slate-10 mt-1">
          指导助手的回复风格与表达方式，让助手以符合品牌调性的方式与客户沟通
        </p>
      </div>

      <!-- 建议规则 -->
      <div v-if="shouldShowGuidelinesSuggestions" class="mb-4">
        <SuggestedRules
          title="快速添加常用指南"
          :items="guidelinesExample"
          :collapsed="guidelinesCollapsed"
          @add="addAllGuidelinesExample"
          @close="closeGuidelinesSuggestions"
          @expand="expandGuidelinesSuggestions"
        >
          <template #default="{ item }">
            <div class="flex items-center justify-between w-full">
              <span class="text-sm text-n-slate-12">{{ item.content }}</span>
              <Button
                :label="
                  hasTemplateRule(guidelines, item.key) ? '已添加' : '添加'
                "
                ghost
                xs
                slate
                class="!text-sm !text-n-slate-11 flex-shrink-0"
                :disabled="hasTemplateRule(guidelines, item.key)"
                @click="addGuideline(item.content, item.key)"
              />
            </div>
          </template>
        </SuggestedRules>
      </div>

      <!-- 规则列表 -->
      <div class="space-y-2">
        <div
          v-if="
            displayGuidelines.length === 0 && !shouldShowGuidelinesSuggestions
          "
          class="text-sm text-n-slate-10 py-2"
        >
          暂无回复指南，请在下方添加
        </div>
        <RuleCard
          v-for="guideline in displayGuidelines"
          :id="guideline.id"
          :key="guideline.id"
          :content="guideline.content"
          :selectable="false"
          @edit="editGuideline"
          @delete="deleteGuideline"
          @hover="isHovered => handleGuidelineHover(isHovered, guideline.id)"
        />
        <AddNewRulesInput
          v-model="newGuideline"
          placeholder="输入新的回复指南"
          label="添加"
          @add="addGuideline"
        />
      </div>
    </section>

    <!-- 开场白 -->
    <section class="bg-[#F3F4F7] dark:bg-n-slate-7/50 rounded-xl p-3">
      <div class="flex items-center gap-2 mb-2">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('PILOT.ASSISTANTS.FORM.GREETING.LABEL') }}
        </h3>
        <span
          v-tooltip="t('PILOT.ASSISTANTS.FORM.GREETING.HELP_TEXT')"
          class="i-lucide-help-circle text-sm text-n-slate-9 cursor-help"
        />
      </div>
      <TextArea
        v-model="greetingContent"
        :max-length="GREETING_MAX_LENGTH"
        :show-character-count="true"
        :auto-height="true"
        min-height="100px"
        :placeholder="t('PILOT.ASSISTANTS.FORM.GREETING.PLACEHOLDER')"
        custom-text-area-wrapper-class="!bg-white dark:!bg-n-slate-800"
        @blur="handleGreetingBlur"
      />
    </section>
  </div>
</template>
